import { useEffect, useRef, useState, type Key } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Input, Select, SelectItem, Spinner } from '@nextui-org/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { ErrorLine, TabIntro } from '../aiAccess/shared';
import { OEMS, type EnrichmentClient, type ProductLookup } from './api';
import { OEM_NAMES, ProductView } from './ProductView';

const ANY = 'any';

/** Seconds since `busy` turned on, ticking while it stays on. */
function useElapsed(busy: boolean): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!busy) return undefined;
    setSeconds(0);
    const started = Date.now();
    const timer = setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);
  return seconds;
}

/**
 * Look up a serial: the unit's product and warranty from the stored data, or from the OEM when
 * nothing recent is stored (~20 s, at most 90 s). `onStored` follows an answer the OEM gave now.
 */
export function LookupSection({ client, onStored }: {
  readonly client: EnrichmentClient | null;
  readonly onStored: () => void;
}) {
  const { t } = useTranslation();
  const [serial, setSerial] = useState('');
  const [oem, setOem] = useState<string>(ANY);
  const [refresh, setRefresh] = useState(false);
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<ProductLookup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const elapsed = useElapsed(busy);
  useEffect(() => () => requestRef.current?.abort(), []);

  const lookUp = async () => {
    const serialNumber = serial.trim();
    if (!client || !serialNumber) return;
    requestRef.current?.abort();
    const request = new AbortController();
    requestRef.current = request;
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      const next = await client.lookup({
        serial_number: serialNumber,
        ...(oem === ANY ? {} : { oem }),
        ...(refresh ? { refresh: true } : {}),
      }, request.signal);
      if (request.signal.aborted) return;
      setAnswer(next);
      if (next.served_from === 'agent') onStored();
    } catch (e) {
      if (!request.signal.aborted) setError((e as Error).message || t('Something went wrong.'));
    } finally {
      if (!request.signal.aborted) setBusy(false);
    }
  };

  return (
    <section aria-label={t('Look up a serial')} className="flex flex-col gap-3">
      <TabIntro title={t('Look up a serial')}>
        <span className="text-sm text-default-500">
          {t('A unit’s product and OEM warranty. Stored answers come back at once; a new serial asks the OEM’s site, which takes about 20 seconds.')}
        </span>
      </TabIntro>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          label={t('Serial number')}
          size="sm"
          className="sm:max-w-xs"
          value={serial}
          onValueChange={setSerial}
          isDisabled={busy}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void lookUp();
            }
          }}
        />
        <Select
          label={t('OEM')}
          size="sm"
          className="sm:max-w-[180px]"
          selectedKeys={[oem]}
          disallowEmptySelection
          onSelectionChange={(keys: 'all' | Set<Key>) => {
            if (keys !== 'all') setOem(String(Array.from(keys)[0] ?? ANY));
          }}
          isDisabled={busy}
        >
          {[
            <SelectItem key={ANY} value={ANY} textValue={t('Detect the OEM')}>{t('Detect the OEM')}</SelectItem>,
            ...OEMS.map((code) => (
              <SelectItem key={code} value={code} textValue={OEM_NAMES[code]}>{OEM_NAMES[code]}</SelectItem>
            )),
          ]}
        </Select>
        <Checkbox size="sm" isSelected={refresh} onValueChange={setRefresh} isDisabled={busy}>
          {t('Ask the OEM again')}
        </Checkbox>
        <CtyButton
          color="primary"
          size="sm"
          className="self-start sm:self-center"
          onPress={() => void lookUp()}
          isDisabled={!client || !serial.trim() || busy}
          isLoading={busy}
        >
          {t('Look up')}
        </CtyButton>
      </div>
      {busy && (
        <div role="status" className="flex items-center gap-2 text-sm text-default-500">
          <Spinner size="sm" />
          {t('Looking up… {{seconds}} s. A serial the OEM is asked about takes about 20 seconds, at most 90.', { seconds: elapsed })}
        </div>
      )}
      <ErrorLine message={error} />
      {answer && <ProductView answer={answer} />}
    </section>
  );
}
